import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faFilter, faCut, faUndo, faTrash, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import MobileTransactionCard from './MobileTransactionCard';
import { useAuth } from '../../AuthContext';
// WeeklyTotalsByType removed — component no longer renders weekly totals

const { rootUrl } = require('../../../../config/config');

const Statement = ({ plate_no, formatDate, formatMoney, handleViewNotes, handleSplitTransaction, handleReverseTransaction, handleDeleteTransaction, permissions = [], transactionTypes = [], refreshKey }) => {

    // Helper to check if a transaction type can be split
    const canSplitType = (transType) => {
        const typeConfig = transactionTypes.find(t => t.value === transType);
        return typeConfig ? typeConfig.can_split !== false : true; // Default to true if not found
    };

    // Helper to check if a transaction type can be reversed
    const canReverseType = (transType) => {
        const typeConfig = transactionTypes.find(t => t.value === transType);
        return typeConfig && typeConfig.reversal_type;
    };
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [tempDateFrom, setTempDateFrom] = useState('');
    const [tempDateTo, setTempDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const { user } = useAuth();

    const storageKey = `car_statement_filters_${user?.id || user?.username || 'anon'}_${plate_no}`;

    // Transaction type & reference filters removed

    const getFirstDateOfCurrentWeek = useCallback(() => {
        const now = new Date();
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek;
        const firstDateOfWeek = new Date(now.setDate(diff));
        return firstDateOfWeek.toISOString().split('T')[0];
    }, []);

    useEffect(() => {
        const init = async () => {
            const first = getFirstDateOfCurrentWeek();
            const today = new Date().toISOString().split('T')[0];

            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    setDateFrom(parsed.dateFrom || first);
                    setTempDateFrom(parsed.dateFrom || first);
                    setDateTo(parsed.dateTo || dateTo || today);
                    setTempDateTo(parsed.dateTo || dateTo || today);
                } else {
                    setDateFrom(first);
                    setTempDateFrom(first);
                    setDateTo(dateTo || today);
                    setTempDateTo(dateTo || today);
                }
            } catch (e) {
                setDateFrom(first);
                setTempDateFrom(first);
                setDateTo(dateTo || today);
                setTempDateTo(dateTo || today);
            }

            // transaction types fetch removed
        };

        init();
    }, [getFirstDateOfCurrentWeek, dateTo, plate_no, storageKey]);

    const saveFilters = (from, to) => {
        try {
            if (!plate_no) return;
            localStorage.setItem(storageKey, JSON.stringify({ dateFrom: from, dateTo: to }));
        } catch (e) {
            // ignore storage errors
        }
    };

    useEffect(() => {
        // Fetch transactions once for the loaded plate. Date filtering is applied client-side
        const fetchTransactions = async () => {
            if (!plate_no) return;
            try {
                setLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';

                const response = await axios.get(`${rootUrl}/cars/${encodeURIComponent(plate_no)}/statement`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: [] }));

                const payload = response?.data || [];
                let rows = payload;
                if (payload && payload.data) rows = payload.data;
                if (!Array.isArray(rows)) rows = [];

                setTransactions(rows);
            } catch (e) {
                console.error('Error fetching transactions:', e);
                setTransactions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, [plate_no, refreshKey]);

    const filteredTransactions = transactions.filter(transaction => {
        const transactionDate = new Date(transaction.date).setHours(0, 0, 0, 0);
        const from = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : null;
        const to = dateTo ? new Date(dateTo).setHours(0, 0, 0, 0) : null;

        return ((!from || transactionDate >= from) && (!to || transactionDate <= to));
    });

    // Compute opening balance (last known post_balance before dateFrom if available)
    const computeOpeningBalance = () => {
        if (!dateFrom) return null;
        const start = new Date(dateFrom).setHours(0, 0, 0, 0);
        const before = transactions
            .filter(t => new Date(t.date).setHours(0, 0, 0, 0) < start)
            .sort((a, b) => new Date(b.date) - new Date(a.date));

        if (before.length === 0) return 0;

        const last = before[0];
        if (last.post_balance !== undefined && last.post_balance !== null && !isNaN(Number(last.post_balance))) {
            return Number(last.post_balance);
        }

        // Fallback: net sum of credits-debits before dateFrom
        return before.reduce((acc, t) => acc + (Number(t.credit || 0) - Number(t.debit || 0)), 0);
    };

    const openingBalance = computeOpeningBalance();

    // Totals for currently filtered transactions
    const totalIn = filteredTransactions.reduce((s, t) => s + Number(t.credit || 0), 0);
    const totalOut = filteredTransactions.reduce((s, t) => s + Number(t.debit || 0), 0);

    // Closing balance: prefer the latest post_balance in the filtered set, else compute from opening + net
    const latestInRange = filteredTransactions.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const closingBalance = (latestInRange && latestInRange.post_balance !== undefined && latestInRange.post_balance !== null && !isNaN(Number(latestInRange.post_balance)))
        ? Number(latestInRange.post_balance)
        : (openingBalance + totalIn - totalOut);

    const fmt = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '-' : formatMoney(v));

    const clearFilters = () => {
        const first = getFirstDateOfCurrentWeek();
        const today = new Date().toISOString().split('T')[0];
        setDateFrom(first);
        setDateTo(today);
        setTempDateFrom('');
        setTempDateTo(today);
        try { localStorage.removeItem(storageKey); } catch (e) { }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-[#2a2e30]">Statement</h3>
                <button
                    onClick={clearFilters}
                    className="px-2 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-sm hover:bg-gray-200 transition-all duration-200 flex items-center"
                >
                    <FontAwesomeIcon icon={faFilter} className="w-3 h-3 mr-1.5" />
                    Clear Filters
                </button>
            </div>

            <div className="bg-gray-50 rounded-sm p-4 mb-6">
                <h4 className="text-xs font-semibold text-[#2a2e30] mb-3">Filter Options</h4>
                <div className="grid grid-cols-1 gap-4">
                    <div className="flex flex-col md:flex-row md:items-end gap-4">
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-[#2a2e30]">Date From</label>
                            <div>
                                <input
                                    type="date"
                                    className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200"
                                    value={tempDateFrom}
                                    onChange={(e) => setTempDateFrom(e.target.value)}
                                    onBlur={() => {
                                        const newFrom = tempDateFrom || getFirstDateOfCurrentWeek();
                                        setDateFrom(newFrom);
                                        saveFilters(newFrom, dateTo || new Date().toISOString().split('T')[0]);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-[#2a2e30]">Date To</label>
                            <div>
                                <input
                                    type="date"
                                    className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200"
                                    value={tempDateTo}
                                    onChange={(e) => setTempDateTo(e.target.value)}
                                    onBlur={() => {
                                        const newTo = tempDateTo || new Date().toISOString().split('T')[0];
                                        setDateTo(newTo);
                                        saveFilters(dateFrom || getFirstDateOfCurrentWeek(), newTo);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* WeeklyTotalsByType removed */}

            {loading ? (
                <div className="flex justify-center items-center space-x-2">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-500" />
                    <span className="text-gray-500">Loading transactions...</span>
                </div>
            ) : (
                <div>
                    {filteredTransactions.length > 0 ? (
                        <div>
                            {/* Mobile list */}
                            <div className="md:hidden space-y-4 mb-4">
                                {filteredTransactions.map((trans) => (
                                    <MobileTransactionCard
                                        key={trans.id}
                                        trans={trans}
                                        plate_no={plate_no}
                                        formatDate={formatDate}
                                        formatMoney={formatMoney}
                                        handleViewNotes={handleViewNotes}
                                    />
                                ))}
                            </div>

                            {/* Desktop/table view */}
                            <div className="hidden md:block overflow-auto max-h-[60vh]">
                                <div className="bg-white rounded-sm shadow-none border border-gray-300 overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 text-sm text-gray-700 sticky top-0 z-20">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                            <div className="mr-4">
                                                <div className="text-xs text-gray-600">Showing</div>
                                                <div className="mt-1 text-xs font-medium text-gray-800">{formatDate(dateFrom)} — {formatDate(dateTo)}</div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-3 mt-3 md:mt-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500 text-xs">Opening</span>
                                                    <span role="status" aria-label="opening-balance" className="inline-block bg-white border border-gray-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-gray-800">{fmt(openingBalance)}</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500 text-xs">In</span>
                                                    <span role="status" aria-label="total-in" className="inline-block bg-green-100 border border-green-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-green-800">{fmt(totalIn)}</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500 text-xs">Out</span>
                                                    <span role="status" aria-label="total-out" className="inline-block bg-red-100 border border-red-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-red-800">{fmt(totalOut)}</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500 text-xs">Closing</span>
                                                    <span role="status" aria-label="closing-balance" className="inline-block bg-white border border-gray-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-gray-900">{fmt(closingBalance)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <table className="w-full md:min-w-[720px] divide-y divide-gray-200">
                                        <thead className="sticky top-16 z-10">
                                            <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Date</th>
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none w-32 max-w-[9.5rem]">Doc No</th>
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none w-36 max-w-[9rem]">Ref</th>
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Type</th>
                                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">In</th>
                                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Out</th>
                                                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Post Bal</th>
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">By</th>
                                                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Notes</th>
                                                {(permissions.includes('roles.manage') || permissions.includes('transactions.split') || permissions.includes('car.trans.reverse') || permissions.includes('car.trans.delete')) && (
                                                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-800 leading-none w-10"></th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {filteredTransactions.map((trans, idx) => (
                                                <tr key={trans.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{formatDate(trans.date)}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs max-w-[9.5rem] truncate" title={trans.doc_no}>
                                                        <Link to={`/cars/${plate_no}/pay/${trans.type}/${trans.doc_no}`} className="text-[#FF5733] hover:text-[#e64a2b] font-medium">
                                                            {trans.doc_no}
                                                        </Link>
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700 max-w-[9rem] truncate" title={trans.your_ref}>{trans.your_ref}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{trans.type}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-right font-mono text-green-600">{formatMoney(trans.credit)}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-right font-mono text-red-600">{formatMoney(trans.debit)}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-right text-gray-700 font-mono">{formatMoney(trans.post_balance)}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{trans.input_by}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                        {trans.notes && (
                                                            <button onClick={() => handleViewNotes(trans)} className="inline-flex items-center text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-150">
                                                                <FontAwesomeIcon icon={faEye} className="w-3 h-3 mr-1" />
                                                                View
                                                            </button>
                                                        )}
                                                    </td>
                                                    {(permissions.includes('roles.manage') || permissions.includes('transactions.split') || permissions.includes('car.trans.reverse') || permissions.includes('car.trans.delete')) && (
                                                        <td className="relative px-2 py-2 text-center">
                                                            <button
                                                                className="w-7 h-7 flex items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                                                                onClick={() => setOpenMenuId(openMenuId === trans.id ? null : trans.id)}
                                                                title="Actions"
                                                            >
                                                                <FontAwesomeIcon icon={faEllipsisVertical} className="w-3 h-3" />
                                                            </button>
                                                            {openMenuId === trans.id && (
                                                                <div className="absolute right-2 top-full z-30 mt-1 w-40 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
                                                                    {(permissions.includes('roles.manage') || permissions.includes('transactions.split')) && canSplitType(trans.type) && (
                                                                        <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50" onClick={() => { handleSplitTransaction(trans); setOpenMenuId(null); }}>
                                                                            <FontAwesomeIcon icon={faCut} className="w-3 h-3 text-[#FF5733]" />
                                                                            Split
                                                                        </button>
                                                                    )}
                                                                    {(permissions.includes('roles.manage') || permissions.includes('car.trans.reverse')) && canReverseType(trans.type) && (
                                                                        <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50" onClick={() => { handleReverseTransaction(trans); setOpenMenuId(null); }}>
                                                                            <FontAwesomeIcon icon={faUndo} className="w-3 h-3 text-orange-500" />
                                                                            Reverse
                                                                        </button>
                                                                    )}
                                                                    {(permissions.includes('roles.manage') || permissions.includes('car.trans.delete')) && (
                                                                        <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50" onClick={() => { handleDeleteTransaction && handleDeleteTransaction(trans); setOpenMenuId(null); }}>
                                                                            <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                                                                            Delete
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No transactions found for the selected criteria.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Statement;
